import React, { useEffect, useState } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import MemberHeader from "./MemberHeader";
import {
  FaArrowAltCircleLeft,
  FaBook,
  FaMobile,
  FaTrophy,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { RamNamBookMember_Position_GetByMemberApi } from "../RamNamBookCRUD";

const downloadApp = [
  {
    color: "#3DDC84",
    link: "https://play.google.com/store/apps/details?id=com.agm.meldidham_member_live&pcampaignid=web_share",
    icon: "fa-brands fa-android",
    title: "Android",
    target: "_blank",
  },
  {
    color: "#000000",
    link: "https://apps.apple.com/us/app/bareja-dham/id6760822771",
    icon: "fa-brands fa-apple",
    title: "iOS",
    target: "_blank",
  },
];
/* ─────────────────────────────────────────────
   Rank Card — one stat tile
───────────────────────────────────────────── */
const RankTile = ({ icon, label, value, color, loading }) => (
  <div
    className="rank-tile"
    style={{
      border: `1.5px solid ${color}22`,
      boxShadow: `0 2px 12px ${color}18`,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        background: `${color}18`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 8px",
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { size: 16, color })}
    </div>

    <div className="rank-value" style={{ color }}>
      {loading ? (
        <span className="rank-skeleton" />
      ) : (
        `${value != null ? value.toLocaleString() : "—"}`
      )}
    </div>

    <div className="rank-label">{label}</div>
  </div>
);
/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const MemberMyProfile = () => {
  const [member, setMember] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);

  // ── Position state — null = not loaded yet, number once loaded
  const [bookPosition, setBookPosition] = useState(null);
  const [digitalPosition, setDigitalPosition] = useState(null);
  const [totalPosition, setTotalPosition] = useState(null);
  const [positionLoading, setPositionLoading] = useState(false);
  const [positionError, setPositionError] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  /* ── Load member from localStorage ── */
  useEffect(() => {
    const stored = localStorage.getItem("customer");
    if (stored) {
      const data = JSON.parse(stored);
      setMember(data);
      setFormData(data);
    }
  }, []);

  /* ── Fetch position once member is loaded ── */
  useEffect(() => {
    if (!member?.ramNamBookMemberID) return;
    fetchPosition(member.ramNamBookMemberID);
  }, [member?.ramNamBookMemberID]);

  const fetchPosition = (memberID) => {
    RamNamBookMember_Position_GetByMemberApi(memberID)
      .then((res) => {
        if (res.data.isSuccess) {
          setBookPosition(res.data.bookPosition);
          setDigitalPosition(res.data.digitalPosition);
          setTotalPosition(res.data.totalPosition);
        }
      })
      .catch(() => {
        setPositionError(true);
      })
      .finally(() => {
        setPositionLoading(false);
      });
  };

  /* ── Handle form field change ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ── Save profile ── */
  const handleSave = async () => {
    try {
      setLoading(true);
      // const res = await updateMemberProfileApi(formData);
      // if (res.data.isSuccess) { localStorage.setItem("customer", JSON.stringify(formData)); }
      localStorage.setItem("customer", JSON.stringify(formData));
      setMember(formData);
      setIsEdit(false);
      toast.success("Profile updated successfully");
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (!member) return null;

  return (
    <>
      <style>
        {" "}
        {`/* ── Animations ── */
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
 
        /* ── Rank tiles: row on all screens, shrink gracefully ── */
        .rank-tiles-row {
          display: flex;
          gap: 8px;
        }
        .rank-tile {
          background: #fff;
          border-radius: 12px;
          padding: 14px 8px;
          text-align: center;
          flex: 1;
          min-width: 0;
        }
        .rank-value {
          font-size: 20px;
          font-weight: 800;
          letter-spacing: -0.5px;
          line-height: 1;
          margin-bottom: 8px;
          word-break: break-all;
        }
        .rank-label {
          font-size: 16px;
          color: #000;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          line-height: 1.2;
        }
        .rank-skeleton {
          display: inline-block;
          width: 48px;
          height: 18px;
          background: #f0f0f0;
          border-radius: 5px;
          animation: pulse 1.4s ease-in-out infinite;
        }
 
        /* ── Larger screens: bigger tiles ── */
        @media (min-width: 480px) {
          .rank-tile { padding: 16px 12px; border-radius: 14px; }
          .rank-value { font-size: 20px; }
          .rank-label { font-size: 11px; }
          .rank-tiles-row { gap: 10px; }
        }
        @media (min-width: 768px) {
          .rank-tile { padding: 18px 14px; }
          .rank-value { font-size: 22px; }
        }
 
        /* ── Profile header: wrap on tiny screens ── */
        .profile-header-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .profile-header-actions {
          display: flex;
          gap: 8px;
          margin-left: auto;
          flex-shrink: 0;
        }
        @media (max-width: 380px) {
          .profile-header-row {
            flex-wrap: wrap;
          }
          .profile-header-actions {
            width: 100%;
            margin-left: 0;
            justify-content: flex-end;
          }
        }
 
        /* ── Card padding: tighter on small ── */
        .profile-card-body {
          padding: 16px;
        }
        @media (min-width: 480px) {
          .profile-card-body { padding: 20px; }
        }
        @media (min-width: 768px) {
          .profile-card-body { padding: 28px; }
        }
 
        /* ── Container: safe side padding on small ── */
        .profile-page-container {
          padding-left: 12px;
          padding-right: 12px;
          padding-top: 20px;
          padding-bottom: 32px;
        }
        @media (min-width: 576px) {
          .profile-page-container { padding-left: 20px; padding-right: 20px; padding-top: 28px; }
        }
 
        /* ── Form fields ── */
        .profile-field .form-control:disabled {
          background-color: #fafafa;
          color: #444;
          border-color: #e8e8e8;
        }
        .profile-field .form-control:not(:disabled) {
          border-color: #FF4F00;
          box-shadow: 0 0 0 2px #FF4F0018;
        }
        .profile-field .form-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #aaa;
          margin-bottom: 4px;
        }
 
        /* ── Retry button ── */
        .rank-retryBtn {
          background: none;
          border: none;
          color: #FF4F00;
          font-size: 12px;
          cursor: pointer;
          text-decoration: underline;
          padding: 0;
        }
 
        /* ── Back button ── */
        .back-btn {
          background: none;
          border: none;
          color: #888;
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0;
          margin-bottom: 12px;
        }
        .back-btn:hover { color: #FF4F00; }
      `}
      </style>

      <MemberHeader />

      <div className="container py-4">
        <Row className="justify-content-center">
          <Col md={8} lg={7}>
            {/* ── Back nav ── */}
            {/* <div className="mb-3">
              <button
                onClick={() =>
                  navigate("/member/dashboard", {
                    state: { activeTab: location.state?.fromTab || "digital" },
                  })
                }
                style={{
                  background: "none",
                  border: "none",
                  color: "#888",
                  fontSize: 13,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: 0,
                }}
              >
                <FaArrowAltCircleLeft size={14} />
                Back to Dashboard
              </button>
            </div> */}

            {/* ── Profile Header Card ── */}
            <Card
              className="shadow-sm mb-3"
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 16,
                overflow: "hidden",
              }}
            >
              {/* Orange top strip */}
              <div
                style={{
                  height: 5,
                  background: "linear-gradient(90deg, #FF4F00, #ff8c42)",
                }}
              />

              <Card.Body className="p-4">
                {/* Avatar + name row */}
                <div className="d-flex align-items-center gap-3 mb-4">
                  <img
                    src="/assets/logo/300-16.jpg"
                    alt="Member"
                    className="rounded-circle border shadow-sm"
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#222",
                        lineHeight: 1.2,
                      }}
                    >
                      {member.memberName || "Member"}
                    </div>
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                      {member.mobileNumber}
                    </div>
                  </div>

                  {/* Edit / Save / Cancel buttons */}

                  <Button
                    size="sm"
                    onClick={() =>
                      navigate("/member/dashboard", {
                        state: {
                          activeTab: location.state?.fromTab || "digital",
                        },
                      })
                    }
                    style={{
                      background: "#FF4F00",
                      border: "none",
                      borderRadius: 8,
                      // fontSize: 12,
                      fontWeight: 600,
                      padding: "3px 6px",
                      // flexShrink: 0,
                      // color: "#888",
                      fontSize: 13,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      // padding: 0,
                    }}
                  >
                    <FaArrowAltCircleLeft size={14} />
                    Back to Dashboard
                  </Button>
                </div>

                {/* ── Ranking / Position Section ── */}
                <div
                  style={{
                    background: "#fdf6f0",
                    borderRadius: 12,
                    padding: "16px 14px",
                    marginBottom: 28,
                    border: "1px solid #ffe8db",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                      color: "#FF4F00",
                      marginBottom: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FaTrophy size={14} />
                    My Rankings
                  </div>

                  {positionError ? (
                    <div
                      style={{
                        textAlign: "center",
                        color: "#bbb",
                        fontSize: 13,
                        padding: "8px 0",
                      }}
                    >
                      Could not load rankings.{" "}
                      <button
                        className="rank-retryBtn"
                        onClick={() => fetchPosition(member.ramNamBookMemberID)}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="rank-tiles-row">
                      <RankTile
                        icon={<FaMobile />}
                        label="डिजिटल क्रम / ડિજિટલ ક્રમ"
                        value={digitalPosition}
                        color="#2563eb"
                        loading={positionLoading}
                      />
                      <RankTile
                        icon={<FaBook />}
                        label="पुस्तक क्रम / પુસ્તક ક્રમ"
                        value={bookPosition}
                        color="#FF4F00"
                        loading={positionLoading}
                      />
                      <RankTile
                        icon={<FaTrophy />}
                        label="कुल क्रम / કુલ ક્રમ"
                        value={totalPosition}
                        color="#059669"
                        loading={positionLoading}
                      />
                    </div>
                  )}

                  <div className="d-flex gap-3 mt-4 text-3xl justify-md-center justify-center fw-bolder">
                    {downloadApp.map((item, index) => (
                      <a
                        key={index}
                        href={item.link}
                        target="_blank"
                        className="btn btn-sm text-white flex items-center gap-2 px-3 "
                        style={{ backgroundColor: item.color }}
                      >
                        <i className={`fs-5 ${item.icon}`}></i> {item.title}
                      </a>
                    ))}
                  </div>
                </div>

                {/* ── Profile Fields ── */}
                <Form>
                  <Row className="g-3">
                    <Col md={12} className="profile-field">
                      <Form.Group>
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                          name="memberName"
                          value={formData.memberName || ""}
                          onChange={handleChange}
                          disabled={!isEdit}
                          placeholder="Enter full name"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="profile-field">
                      <Form.Group>
                        <Form.Label>Mobile Number</Form.Label>
                        <Form.Control
                          value={formData.mobileNumber || ""}
                          disabled
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="profile-field">
                      <Form.Group>
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          value={formData.countryName || ""}
                          disabled
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="profile-field">
                      <Form.Group>
                        <Form.Label>Village / City</Form.Label>
                        <Form.Control
                          name="villageCityName"
                          value={formData.villageCityName || ""}
                          onChange={handleChange}
                          disabled={!isEdit}
                          placeholder="Enter city or village"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} className="profile-field">
                      <Form.Group>
                        <Form.Label>Pincode</Form.Label>
                        <Form.Control
                          name="pincode"
                          value={formData.pincode || ""}
                          onChange={handleChange}
                          disabled={!isEdit}
                          placeholder="Enter pincode"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12} className="profile-field">
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="addressAndText"
                          value={formData.addressAndText || ""}
                          onChange={handleChange}
                          disabled={!isEdit}
                          placeholder="Enter full address"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default MemberMyProfile;
